import { useEffect, useRef, useState } from "react";
import { pdfjs } from "../lib/pdfjs";

interface Props {
  url: string;
  /** Max width per rendered page (px). Defaults to 720. */
  maxWidth?: number;
  className?: string;
}

/**
 * Streams a PDF from a signed URL and renders every page into stacked canvases
 * so the user can scroll through the lease before signing.
 *
 * Re-renders on `url` change. Errors are surfaced to the parent via a small
 * inline message and a console error.
 */
export function PdfPreview({ url, maxWidth = 720, className }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pages, setPages] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setLoaded(false);

    async function render() {
      try {
        const host = hostRef.current;
        if (!host) return;
        host.innerHTML = "";
        const task = pdfjs.getDocument({ url, withCredentials: false });
        const doc = await task.promise;
        if (cancelled) return;
        setPages(doc.numPages);
        for (let p = 1; p <= doc.numPages; p++) {
          if (cancelled) return;
          const page = await doc.getPage(p);
          const viewport = page.getViewport({ scale: 1 });
          const scale = Math.min(maxWidth / viewport.width, 2);
          const scaled = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          canvas.width = Math.floor(scaled.width);
          canvas.height = Math.floor(scaled.height);
          canvas.className =
            "block mx-auto mb-4 rounded-lg shadow-sm border border-slate-200 bg-white max-w-full";
          host.appendChild(canvas);
          const ctx = canvas.getContext("2d");
          if (!ctx) continue;
          await page.render({ canvasContext: ctx, viewport: scaled }).promise;
        }
        if (!cancelled) setLoaded(true);
      } catch (err) {
        console.error("PdfPreview render failed", err);
        if (!cancelled) setError(String((err as Error)?.message ?? err));
      }
    }
    void render();

    return () => {
      cancelled = true;
    };
  }, [url, maxWidth]);

  return (
    <div className={className}>
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load lease PDF: {error}
        </div>
      ) : null}
      <div ref={hostRef} aria-busy={!loaded} aria-live="polite" />
      {!loaded && !error ? (
        <div className="py-8 text-center text-sm text-slate-500">
          Loading lease…
        </div>
      ) : null}
      {loaded && pages > 0 ? (
        <p className="text-xs text-slate-500 text-center mt-2">
          {pages} page{pages > 1 ? "s" : ""} loaded.
        </p>
      ) : null}
    </div>
  );
}
